import React, { useState, useEffect, useCallback, useMemo } from 'react';

import Loader from '../Loader/Loader';
import SummaryElement from './SummaryElement';

import { StyledUserPanelSummary } from '../../atoms/UserPanelSummary/StyledUserPanelSummary';

import {
    listHoursSummary,
    listStudents,
    listSubjects,
    listTrainers,
} from '../../../logic/requests/students';
import {
    StyledLoaderWrapper,
    StyledGlobalScrollWrapper,
    StyledGlobalHeadingWrapper,
} from '../../../styles/sharedStyles';

import { getLang, getString } from '../../../strings';
import { cebulaCompare } from '../../../logic/arrays';
import useScrollBar from '../../../logic/hooks/useScrollBar';
import UserPanelHeadingText from '../../atoms/UserPanel/UserPanelHeadingText';
import { listManagerHoursTypes, listManagers } from '../../../logic/requests/employees';
import { hasPermission } from '../../../logic/permissions';
import CurrentMonthSummary from './CurrentMonthSummary';

const UserPanelSummary = ({ user }) => {
    const { isScrollBar, scrollElement, trigger } = useScrollBar();
    const [summary, setSummary] = useState();
    const [students, setStudents] = useState();
    const [trainers, setTrainers] = useState();
    const [subjects, setSubjects] = useState();
    const [managers, setManagers] = useState();
    const [managerHoursTypes, setManagerHoursTypes] = useState();

    const canManageOwnManagerHours = useMemo(
        () => hasPermission(user, 'can_have_manager_hours_and_access_own_hours'),
        [user]
    );

    const canManageAllManagerHours = useMemo(
        () => hasPermission(user, 'can_manage_all_manager_hours'),
        [user]
    );

    const fetchManagers = useCallback(() => {
        listManagers()
            .then((res) => {
                setManagers(
                    res.sort((a, b) =>
                        cebulaCompare(
                            `${a.lastname} ${a.firstname}`,
                            `${b.lastname} ${b.firstname}`
                        )
                    )
                );
            })
            .catch((err) => {
                console.error('listManagers', err);
            });
    }, [setManagers]);

    const fetchManagerHoursTypes = useCallback(() => {
        listManagerHoursTypes()
            .then((res) => {
                setManagerHoursTypes(res);
            })
            .catch((err) => {
                console.error('listManagerHoursTypes', err);
            });
    }, [setManagerHoursTypes]);

    const fetchSummary = useCallback(() => {
        listHoursSummary()
            .then((res) => {
                setSummary(res);
            })
            .catch((err) => {
                console.error('listHoursSummary', err);
            });
    }, [setSummary]);

    useEffect(() => {
        fetchSummary();
        listStudents()
            .then((res) => {
                setStudents(
                    res.sort((a, b) =>
                        cebulaCompare(
                            `${a.lastname} ${a.firstname}`,
                            `${b.lastname} ${b.firstname}`
                        )
                    )
                );
            })
            .catch((err) => {
                console.error('listStudents', err);
            });

        listTrainers()
            .then((res) => {
                setTrainers(
                    res.sort((a, b) =>
                        cebulaCompare(
                            `${a.lastname} ${a.firstname}`,
                            `${b.lastname} ${b.firstname}`
                        )
                    )
                );
            })
            .catch((err) => {
                console.error('listTrainers', err);
            });

        listSubjects()
            .then((res) => {
                setSubjects(
                    res
                        .filter((el) => el.description)
                        .sort((a, b) =>
                            cebulaCompare(a.description[getLang()], b.description[getLang()])
                        )
                );
            })
            .catch((err) => {
                console.error('listSubjects', err);
            });

        if (canManageOwnManagerHours || canManageAllManagerHours) {
            fetchManagerHoursTypes();
        }

        if (canManageAllManagerHours) {
            fetchManagers();
        }
    }, [
        canManageAllManagerHours,
        canManageOwnManagerHours,
        fetchManagerHoursTypes,
        fetchManagers,
        fetchSummary,
        setSummary,
    ]);

    return (
        <StyledUserPanelSummary>
            {!(summary && students && trainers && subjects) ? (
                <StyledLoaderWrapper>
                    <Loader />
                </StyledLoaderWrapper>
            ) : (
                <>
                    <CurrentMonthSummary hours={summary} /> 
                    <StyledGlobalHeadingWrapper>
                        <UserPanelHeadingText
                            text={`${getString('User_panel_summary_summary')}:`}
                        />
                    </StyledGlobalHeadingWrapper>
                    <StyledGlobalScrollWrapper $isscrollbar={isScrollBar} ref={scrollElement}>
                        <div>
                            {Object.entries(summary).map(([id, month], index) => (
                                <SummaryElement
                                    id={id}
                                    key={id}
                                    month={month}
                                    setMonth={(val) =>
                                        setSummary((cur) => {
                                            cur[id] =
                                                typeof val === 'function' ? val(cur[id]) : val;
                                            return { ...cur };
                                        })
                                    }
                                    onDelete={fetchSummary}
                                    students={students}
                                    trainers={trainers}
                                    subjects={subjects}
                                    managers={managers}
                                    managerHourTypes={managerHoursTypes}
                                    trigger={trigger}
                                    user={user}
                                    firstElement={index === 0}
                                    hours={summary}
                                />
                            ))}
                        </div>
                    </StyledGlobalScrollWrapper>
                </>
            )}
        </StyledUserPanelSummary>
    );
};

export default UserPanelSummary;
